import React, { forwardRef, Fragment } from 'react';
import { Box, Button, Flex, Grid, Text } from 'theme-ui';
import CartQuantitySelector from '~/components/Cart/QuantitySelector';
import ImgLoader from '../../Generic/ImgLoader';
import ShippingNote from '../../Generic/ShippingNote';
import { displayOptionValues } from '~/utils/display-option-values';
import { Image } from '~/types';
import TrashIcon from '~/assets/images/icons/trash.svg';
import { GiftCardPresentation } from './GiftCardLineItem';
import SpinnerIcon from '~/components/Spinner/SpinnerIcon';

type Props = {
  image: Image;
  title: string | null | undefined;
  size?: string | null;
  color?: string | null;
  quantity: number;
  id: string | number;
  priceWithoutDecimal?: string;
  optionalShippingNote?: string | null;
  confirmed?: boolean;
  kit?: boolean;
  isGiftCard?: boolean;
  giftCards?: GiftCardPresentation[];
  amountPresentation?: string;
  removeGiftCard?: (giftCardId: number) => Promise<void>;
  isRemovingGiftCard?: number | null;
  isRemovingGiftCardError?: { id: number; error: string } | null;
};

const LineItemGeneric = forwardRef<HTMLDivElement, Props>(function LineItemGeneric(
  {
    image,
    title,
    size,
    color,
    quantity,
    id,
    priceWithoutDecimal,
    optionalShippingNote,
    confirmed,
    kit,
    isGiftCard,
    giftCards,
    amountPresentation,
    removeGiftCard,
    isRemovingGiftCard,
    isRemovingGiftCardError,
  },
  ref
) {
  return (
    <Box
      ref={ref}
      sx={{
        background: 'white',
        width: '100%',
        borderRadius: !kit && '1.6rem 0 1.6rem 0',
        overflow: 'hidden',
        WebkitMaskImage: '-webkit-radial-gradient(white, black)',
      }}
    >
      <Flex
        className="list-container show"
        sx={{
          width: '100%',
          background: 'white',
          height: '6.4rem',
          color: 'black',
        }}
      >
        {image && (
          <Box sx={{ width: '6.4rem', height: '6.4rem', position: 'relative' }}>
            <Box sx={{ width: '100%', top: 0, left: 0, height: 0, pb: '100%', position: 'absolute' }}>
              <ImgLoader
                image={image}
                forwardSx={{ height: 'auto', width: '100%', position: 'relative', objectFit: 'cover' }}
              />
            </Box>
          </Box>
        )}
        <Flex sx={{ p: '1.6rem', justifyContent: 'space-between', width: '100%', flex: 1 }}>
          <Flex sx={{ flexDirection: 'column', justifyContent: 'flex-start', gap: '3px' }}>
            <Text sx={{ fontSize: '1.6rem', textTransform: 'uppercase', fontWeight: '500', lineHeight: '1.6rem' }}>
              {title}
            </Text>
            {isGiftCard
              ? amountPresentation && (
                  <Text
                    sx={{
                      fontSize: '1rem',
                      textTransform: 'uppercase',
                      lineHeight: '1rem',
                      fontWeight: 'medium',
                    }}
                    as="p"
                  >
                    {amountPresentation}
                  </Text>
                )
              : (size || color) && (
                  <Text
                    sx={{
                      fontSize: '1rem',
                      textTransform: 'uppercase',
                      lineHeight: '1rem',
                      fontWeight: 'medium',
                    }}
                    as="p"
                  >
                    {displayOptionValues({ size, color })}
                  </Text>
                )}
          </Flex>
          {!kit && (
            <Flex sx={{ gap: '1.6rem' }}>
              <Flex sx={{ flexDirection: 'column', justifyContent: 'start', gap: '8px' }}>
                <Text
                  sx={{
                    fontSize: '1rem',
                    textTransform: 'uppercase',
                    fontWeight: '400',
                    lineHeight: '100%',
                    color: 'UIGray2',
                  }}
                >
                  Quantity
                </Text>
                {confirmed || isGiftCard ? (
                  <Text
                    sx={{
                      fontSize: '1.2rem',
                      textTransform: 'uppercase',
                      lineHeight: '1.2rem',
                      height: '1.8rem',
                      fontWeight: 'medium',
                    }}
                    as="p"
                  >
                    {quantity}
                  </Text>
                ) : (
                  <CartQuantitySelector
                    forwardSx={{ p: 0, m: 0, lineHeight: 0 }}
                    aria-label="quantity selector"
                    label="quantity"
                    quantity={quantity}
                    lineItemId={id}
                  />
                )}
              </Flex>
              <Flex sx={{ flexDirection: 'column', justifyContent: 'start', gap: '8px' }}>
                <Text
                  sx={{
                    fontSize: '1rem',
                    textTransform: 'uppercase',
                    fontWeight: '400',
                    lineHeight: '100%',
                    color: 'UIGray2',
                  }}
                >
                  Price
                </Text>
                <Text
                  sx={{ fontSize: '1.2rem', textTransform: 'uppercase', lineHeight: '1.2rem', fontWeight: 'medium' }}
                  as="p"
                >
                  {priceWithoutDecimal}
                </Text>
              </Flex>
            </Flex>
          )}
        </Flex>
      </Flex>
      {!kit && optionalShippingNote && <ShippingNote note={optionalShippingNote} confirmed={confirmed} />}
      {isGiftCard && giftCards && (
        <div>
          {giftCards.map((giftCard) => {
            const giftCardEntries = Object.entries(giftCard);
            return (
              <Grid
                key={giftCard.id}
                sx={{
                  gridTemplateColumns: '6.4rem 1fr fit-content(1.6rem)',
                  columnGap: '1.6rem',
                  rowGap: '.4rem',
                  borderTop: '1px solid',
                  borderColor: 'cloud',
                  py: '.8rem',
                }}
              >
                {giftCardEntries
                  .filter(([key, value]) => key !== 'id' && value)
                  .map(([key, value]) => (
                    <Fragment key={`${key}-${value}`}>
                      <Text
                        sx={{
                          gridColumn: '1/2',
                          fontSize: '1rem',
                          lineHeight: '1rem',
                          fontWeight: 'medium',
                          textAlign: 'right',
                          color: 'black',
                        }}
                      >
                        {key}:
                      </Text>
                      <Text sx={{ gridColumn: '2/3', fontSize: '1rem', lineHeight: '1rem', color: 'black' }}>
                        {value}
                      </Text>
                    </Fragment>
                  ))}
                {!confirmed && (
                  <Button
                    onClick={() => removeGiftCard?.(giftCard.id)}
                    variant="bare"
                    sx={{ gridColumn: '3/4', gridRow: '1/3' }}
                  >
                    {isRemovingGiftCard === giftCard.id ? <SpinnerIcon size={11} color="black" /> : <TrashIcon />}
                  </Button>
                )}
                {isRemovingGiftCardError && isRemovingGiftCardError.id === giftCard.id && (
                  <Text as="p" sx={{ gridColumn: '1/3', color: 'red', fontSize: '1.1rem', lineHeight: 1, px: '.4rem' }}>
                    {isRemovingGiftCardError.error}
                  </Text>
                )}
              </Grid>
            );
          })}
        </div>
      )}
    </Box>
  );
});

export default LineItemGeneric;
